import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { monthLabel } from '../lib/format';
import { currentPeriod } from '../lib/period';
import { useAddSheet } from './AddSheet';
import { ThemeToggle } from './ThemeToggle';

/**
 * Guscio dell'app, secondo le linee guida Apple: barra delle schede in basso
 * con cinque destinazioni (niente azioni dentro la barra), titolo in linea in
 * alto con "Altro" a sinistra e "+" a destra; da 1024px in su colonna
 * laterale in stile Mac con barra degli strumenti.
 */
const DESTINATIONS = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/mese', label: 'Mese', icon: ListIcon },
  { to: '/statistiche', label: 'Statistiche', icon: ChartIcon },
  { to: '/budget', label: 'Budget', icon: TargetIcon },
  { to: '/liquidita', label: 'Liquidità', icon: WalletIcon },
];

const TITLES: Record<string, string> = {
  '/mese': 'Mese',
  '/statistiche': 'Statistiche',
  '/budget': 'Budget',
  '/liquidita': 'Liquidità',
  '/altro': 'Altro',
};

function usePageTitle(): string {
  const { pathname } = useLocation();
  const period = currentPeriod();
  return TITLES[pathname] ?? monthLabel(period.year, period.month);
}

export function AppShell() {
  const addSheet = useAddSheet();
  const title = usePageTitle();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Il + e' contestuale: in Liquidita' registra un saldo, altrove un movimento.
  const onLiquidity = pathname === '/liquidita';
  const addLabel = onLiquidity ? 'Nuovo saldo' : 'Nuovo movimento';
  const add = () => (onLiquidity ? navigate('/liquidita?nuovo=1') : addSheet.open());

  return (
    <div className="min-h-dvh lg:flex">
      <aside className="hidden w-56 shrink-0 border-r border-border bg-[var(--mat)] p-3 backdrop-blur-xl lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col">
        <div className="mb-4 flex items-center gap-2 px-2 pt-1">
          <img src="/icon.svg" alt="" className="h-7 w-7" />
          <span className="text-sm font-bold">ExpenseMonitor</span>
        </div>
        <nav className="flex flex-col gap-0.5">
          {[...DESTINATIONS, { to: '/altro', label: 'Altro', icon: DotsIcon, end: false }].map(
            ({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[0.8125rem] transition',
                    isActive ? 'bg-accent text-accent-text' : 'hover:bg-surface-2',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={isActive ? 'text-accent-text' : 'text-accent'}>
                      <Icon size={17} />
                    </span>
                    {label}
                  </>
                )}
              </NavLink>
            ),
          )}
        </nav>
        <div className="mt-auto px-1">
          <ThemeToggle />
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 grid h-12 grid-cols-[2.25rem_1fr_2.25rem] items-center bg-bg px-4 lg:h-[3.25rem] lg:grid-cols-[1fr_auto] lg:border-b lg:border-border lg:bg-[var(--mat)] lg:px-7 lg:backdrop-blur-xl">
          <NavLink
            to="/altro"
            aria-label="Altro"
            className={({ isActive }) =>
              clsx(
                'flex h-9 w-9 items-center justify-center rounded-full transition lg:hidden',
                isActive ? 'bg-accent text-accent-text' : 'bg-surface-2 text-accent',
              )
            }
          >
            <DotsIcon size={18} />
          </NavLink>
          <h1 className="truncate text-center text-[1.0625rem] font-semibold lg:text-left lg:text-[0.9375rem] lg:font-bold">
            {title}
          </h1>
          <button
            type="button"
            onClick={add}
            aria-label={onLiquidity ? 'Aggiungi saldo' : 'Aggiungi movimento'}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-text transition active:scale-95 lg:hidden"
          >
            <PlusIcon />
          </button>
          <button
            type="button"
            onClick={add}
            className="hidden rounded-[var(--radius-control)] bg-accent px-3 py-1.5 text-[0.8125rem] font-semibold text-accent-text transition active:scale-95 lg:block"
          >
            + {addLabel}
          </button>
        </header>

        <main className="mx-auto w-full max-w-5xl px-4 pt-3 pb-28 lg:px-7 lg:pt-5 lg:pb-10">
          <Outlet />
        </main>
      </div>

      <nav
        aria-label="Navigazione principale"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-[var(--mat)] backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto grid max-w-lg grid-cols-5 px-1 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {DESTINATIONS.map((d) => (
            <TabLink key={d.to} {...d} />
          ))}
        </div>
      </nav>
    </div>
  );
}

function TabLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        clsx(
          'flex min-h-11 flex-col items-center gap-0.5 rounded-lg py-1 text-[0.625rem] font-medium transition',
          isActive ? 'text-accent' : 'text-muted',
        )
      }
    >
      <Icon size={24} />
      {label}
    </NavLink>
  );
}

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function HomeIcon({ size = 22 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </svg>
  );
}

function ListIcon({ size = 22 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
    </svg>
  );
}

function ChartIcon({ size = 22 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

function TargetIcon({ size = 22 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function WalletIcon({ size = 22 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H19v3" />
      <path d="M3 7.5V18a1.5 1.5 0 0 0 1.5 1.5H20a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H5.5A2.5 2.5 0 0 1 3 7.5Z" />
      <path d="M16.5 13.5h.01" />
    </svg>
  );
}

function DotsIcon({ size = 22 }: { size?: number }) {
  return (
    <svg {...iconProps} width={size} height={size}>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg {...iconProps} width={18} height={18} strokeWidth={2.4}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
